"use client";

import { useEffect, useRef, useState } from "react";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right" | "none";
  duration?: number;
  once?: boolean;
}

const hiddenOffset: Record<NonNullable<ScrollRevealProps["direction"]>, string> = {
  up: "translate-y-10",
  down: "-translate-y-10",
  left: "translate-x-10",
  right: "-translate-x-10",
  none: "",
};

/**
 * Gorunur alana girince icerigi yumusakca acar.
 *
 * framer-motion ile yaziliydi; hakkimizda ve iletisim sayfalarinin tum icerigini
 * sardigi icin motion paketi bu sayfalara ve onlara link veren her sayfanin
 * prefetch'ine giriyordu (PageSpeed: 44 KB, 37 KB kullanilmayan, 28 Eyl 2026).
 * Ayni etki IntersectionObserver + CSS gecisiyle; prefers-reduced-motion'a uyar.
 */
export default function ScrollReveal({
  children,
  className = "",
  delay = 0,
  direction = "up",
  duration = 0.6,
  once = true,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting);
      if (visible) { setInView(true); if (once) io.disconnect(); }
      else if (!once) setInView(false);
    }, { rootMargin: "-60px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [once]);

  return (
    <div
      ref={ref}
      className={`transition-[opacity,transform] ease-[cubic-bezier(0.25,0.1,0.25,1)] motion-reduce:transition-none ${inView ? "translate-x-0 translate-y-0 opacity-100" : `opacity-0 ${hiddenOffset[direction]}`} ${className}`}
      style={{ transitionDuration: `${duration}s`, transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}
